// src/components/NoteCard.js
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckIcon, MapPinIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { UserIcon, UsersIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types'; // For prop type checking
import { useTranslation } from 'react-i18next';
import ImageThumbnailStack from './ImageThumbnailStack';

function NoteCard({ note, toggleCompletion }) {
  const { t } = useTranslation();
  
  // Function to format Firestore timestamp - short date time format like iOS
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    try {
      // Format like iOS: "Jan 15, 2024, 3:30 PM"
      return format(date, 'MMM d, yyyy, h:mm a');
    } catch {
      return format(date, 'MMM d, yyyy');
    }
  };

  const isProcessing = note.processingStatus > 0 && note.processingStatus < 200;
  const isLinkNote = note.hasLinks && note.links && note.links.length > 0;
  
  // Get text content - prioritize summary_short, then summary, then transcript
  const getText = () => {
    if (note.summary_short && note.summary_short.trim()) {
      return note.summary_short;
    } else if (note.summary && note.summary.trim()) {
      return note.summary;
    } else if (note.transcript && note.transcript.trim()) {
      return note.transcript;
    }
    return '';
  };

  return (
    <div className={`${note.isPinned ? 'bg-yellow-50' : 'bg-white'} shadow-sm rounded-lg p-4 hover:shadow-md transition-shadow duration-200 border border-gray-200 relative`}>
      {/* Pin icon for pinned notes */}
      {note.isPinned && (
        <MapPinIcon className="absolute top-2 right-2 w-4 h-4 text-yellow-600" />
      )}
      <div className="flex items-start gap-3">
        {/* Task Completion Indicator for Todos - vertically centered */}
        {note.noteType === 0 && (
          <div
            className="flex-shrink-0 cursor-pointer self-center"
            onClick={(e) => {
              e.stopPropagation();
              toggleCompletion(note.id, note.isTodoCompleted);
            }}
            title={note.isTodoCompleted ? t('notes.actions.markAsIncomplete') : t('notes.actions.markAsComplete')}
            aria-label={note.isTodoCompleted ? t('notes.actions.markAsIncomplete') : t('notes.actions.markAsComplete')}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                toggleCompletion(note.id, note.isTodoCompleted);
              }
            }}
          >
            {note.isTodoCompleted ? (
              <div className="w-6 h-6 flex items-center justify-center bg-orange-500 text-white rounded-full">
                <CheckIcon className="w-3.5 h-3.5" />
              </div>
            ) : (
              <span className="w-6 h-6 inline-block border-2 border-gray-400 rounded-full"></span>
            )}
          </div>
        )}
        
        {/* Content Section */}
        <div className="flex-1 min-w-0">
          {isProcessing ? (
            // Processing State
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-gray-600 font-medium">{t('notes.status.processing')}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-gray-600 h-1.5 rounded-full transition-all duration-300" 
                  style={{ width: `${note.processingProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {note.processingStatus === 0 ? t('notes.status.notStarted') :
                 note.processingStatus === 5 ? t('notes.status.uploading') :
                 note.processingStatus === 10 ? t('notes.status.transcribing') :
                 note.processingStatus === 21 ? t('notes.status.summarizing') :
                 note.processingStatus === 22 ? t('notes.status.extractingMetadata') :
                 note.processingStatus === 30 ? t('notes.status.sendingEmail') :
                 note.processingStatus === 200 ? t('notes.status.completed') :
                 note.processingStatus === 400 ? t('notes.status.error') :
                 note.processingStatus === 401 ? t('notes.status.offline') :
                 note.processingStatus === 402 ? t('notes.status.nospeech') :
                 t('notes.status.processing')} {note.processingProgress}%
              </p>
            </div>
          ) : (note.processingStatus === 400 || note.processingStatus === 402) ? (
            // Error State
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-gray-900">
                  {note.processingStatus === 402 ? t('notes.status.empty') : t('notes.status.notsent')}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {formatDate(note.creationDate)} · {note.processingStatus === 402 ? t('notes.status.noTextFound') : t('notes.status.error_processing')}
              </p>
            </div>
          ) : isLinkNote ? (
            // Link Note State
            <div className="flex items-center gap-3">
               {/* Image or Placeholder */}
               <div className="flex-shrink-0">
                 {note.links[0].imageURL ? (
                   <img 
                     src={note.links[0].imageURL} 
                     alt={note.links[0].title || t('toasts.linkPreview')} 
                     className="w-[55px] h-[55px] md:w-[66px] md:h-[66px] rounded-lg object-cover bg-gray-100 border border-gray-200"
                     onError={(e) => {
                       e.target.onerror = null; 
                       e.target.style.display = 'none';
                       e.target.nextSibling.style.display = 'flex';
                     }}
                   />
                 ) : null}
                 <div className={`w-[55px] h-[55px] md:w-[66px] md:h-[66px] rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center ${note.links[0].imageURL ? 'hidden' : ''}`}>
                   <ArrowTopRightOnSquareIcon className="w-6 h-6 text-gray-400" />
                 </div>
               </div>

               {/* Content */}
               <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {note.emoji && <span className="text-lg md:text-base flex-shrink-0">{note.emoji}</span>}
                    <h2 className="text-base md:text-sm flex-1 truncate font-semibold text-gray-900">
                      {note.title || t('notes.status.processing')}
                    </h2>
                  </div>
                  
                  <div className="text-sm md:text-xs text-gray-500 line-clamp-2">
                    {note.links[0].title && note.links[0].title !== 'loading...' 
                      ? note.links[0].title 
                      : (getText() || note.links[0].url)}
                  </div>
               </div>
            </div>
          ) : (
            // Normal State
            <div className="space-y-1">
              {note.noteType === 0 ? (
                // Compact format for TODO tasks - 2 lines only
                <>
                  {/* Line 1: Emoji + Title */}
                  <div className="flex items-center gap-2">
                    {note.emoji && <span className="text-lg md:text-base flex-shrink-0">{note.emoji}</span>}
                    <h2 className={`text-base md:text-sm flex-1 truncate ${note.isTodoCompleted ? 'line-through text-gray-500 font-normal' : 'font-semibold text-gray-1000'}`}>
                      {note.title || t('notes.status.processing')}
                    </h2>
                  </div>
                  {/* Line 2: Summary short or summary - smaller text */}
                  <div className="flex items-start gap-2">
                    {getText() && (
                      <div className="flex-1 min-w-0 text-sm md:text-xs text-gray-500 truncate leading-tight">
                        <div className="text-sm md:text-[13px] max-w-none leading-tight line-clamp-1">
                          <ReactMarkdown>{getText()}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                    {note.images && note.images.length > 0 && (
                       <div className="pt-0 pl-1">
                         <ImageThumbnailStack images={note.images} uid={note.uid} />
                       </div>
                    )}
                  </div>
                </>
              ) : (
                // Regular notes format with more info
                <>
                  {/* First Line: Emoji + Title */}
                  <div className="flex items-center gap-2">
                    {note.emoji && <span className="text-lg md:text-base flex-shrink-0">{note.emoji}</span>}
                    <h2 className="text-base md:text-sm flex-1 truncate font-semibold text-gray-900">
                      {note.title || t('notes.status.processing')}
                    </h2>
                  </div>
                  
                  {/* Date */}
                  <p className="text-sm md:text-xs text-gray-500 font-medium">{formatDate(note.creationDate)}</p>
                  
                  {/* Text Content & Images */}
                  <div className="flex items-start gap-2">
                    {getText() && (
                      <div className="flex-1 min-w-0 text-base md:text-sm text-gray-600 line-clamp-4">
                        <div className="prose prose-sm max-w-none [&>*]:my-0 [&>p]:my-0">
                          <ReactMarkdown>{getText()}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                    
                    {note.images && note.images.length > 0 && (
                      <div className="pt-1 pl-1">
                         <ImageThumbnailStack images={note.images} uid={note.uid} />
                      </div>
                    )}
                  </div>
                  
                  {/* Tags + Contacts + Folder at bottom right */}
                  {(note.tags?.length > 0 || (Array.isArray(note.contacts) && note.contacts.length > 0) || (note.folderId && note.folderId !== 'inbox' && note.folderId !== 'archived')) && (
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Contacts */}
                        {Array.isArray(note.contacts) && note.contacts.length > 0 && (
                            <div 
                                className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-md text-sm md:text-xs text-gray-600 border border-gray-200"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {note.contacts.length === 1 ? <UserIcon className="w-3.5 md:w-3 h-3.5 md:h-3"/> : <UsersIcon className="w-3.5 md:w-3 h-3.5 md:h-3"/>}
                                {note.contacts.map((c, i) => (
                                    <React.Fragment key={c.id}>
                                        <Link to={`/contacts/${c.id}`} className="hover:text-orange-600 hover:underline">
                                            {c.contactName}
                                        </Link>
                                        {i < note.contacts.length - 1 && ', '}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {/* Tags on the left */}
                        {note.tags?.map((tag, index) => (
                          <span key={index} className="bg-blue-100 text-blue-700 text-sm md:text-xs px-2 py-0.5 rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      {/* Folder on the right */}
                      {note.folderId && note.folderId !== 'inbox' && note.folderId !== 'archived' && (
                        <span className="text-sm md:text-xs text-gray-400 flex-shrink-0">
                          {note.folderName || note.folderId}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Update propTypes validation
NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.string.isRequired,
    emoji: PropTypes.string,
    title: PropTypes.string.isRequired,
    creationDate: PropTypes.object.isRequired, // Firestore Timestamp
    summary: PropTypes.string,
    summary_short: PropTypes.string,
    transcript: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    noteType: PropTypes.number.isRequired,
    isTodoCompleted: PropTypes.bool,
    isProcessing: PropTypes.bool,
    processingStatus: PropTypes.number,
    processingProgress: PropTypes.number,
    folderId: PropTypes.string,
    folderName: PropTypes.string,
    uid: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    isPinned: PropTypes.bool,
  }).isRequired,
  toggleCompletion: PropTypes.func.isRequired,
};

export default NoteCard;
